import { useRef, useState } from 'react';
import type { DemoKey, DemoScript, Fx, RoundProps, ViewProps } from '../kit.js';
import { BOARD, BoardButton, BoardFrame, INK, OK, BAD, TYPE, mono, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import type { Lang } from '../../../ui/text.js';
import { ERA_SKIN } from '../../art.js';
import type { Era } from '../../art.js';
import { BELTS, CRATE_W, answerRow, dealStock } from './logic.js';
import type { Mark, Stock, TargetMark } from './logic.js';
import { CRATE_PAL, shade } from './shared.js';

/**
 * QUARTERMASTER · COUNT THE STOCK — count the marked crates as they roll past.
 *
 * Crates come out of a hatch on the left and ride a belt into the hatch on the right; the sign
 * over the belt shows the mark to count. When the belt stops, tap how many from the row of numbers
 * under it. A wrong number is the mistake: the right one is shown, and a fresh lot rolls past.
 * Round 1 is six crates, the mark or nothing; round 2 is seven, a little quicker; round 3 is eight,
 * quicker again, with other marks among them. (A round with the mark's look-alike among them — a
 * four-point star beside the five-point one, a triangle upside down, a cross turned — is still in
 * the rules; no round deals it since 6 Oct 2026.)
 *
 * A converted board (kit.tsx): a small view state (`CountView`), a pure `View`, the `Board` that
 * runs the belt on the game clock, and the `demo` the how-to card plays.
 */

const W = BOARD.w;
/** The hatches at either end. */
const HATCH_W = 30;
/** A crate on the belt, in units. */
const CW = Math.round(CRATE_W * W);
const CH = Math.round(CW * 0.86);
/** The belt's top, where the crates stand, and the foot of the scene. */
const BELT_Y = 170;
const SCENE_H = 224;
/** The row of numbers below the belt. */
const BTN_Y = 244;
const BTN_H = 58;
const BTN_SIDE = 10;
const BTN_GAP = 6;
/** The rollers under the belt: a half turn of one is one ridge of the belt, so the belt's run can be sent whole. */
const RIDGE = 16;
const ROLLER_R = RIDGE / Math.PI;
/** How long a wrong answer stays on the board before a fresh lot rolls. */
const AGAIN_MS = 1_900;

/** Every mark a crate can carry, by its number in a view state. */
export const MARKS: readonly Mark[] = ['star', 'spark', 'tri', 'vee', 'plus', 'cross', 'dot', 'ring', 'square'];

/** The marks to count, named: in English, and in bokmål as "count the …" and "how many …?". */
const NAMES: Record<TargetMark, { en: string; the: string; many: string }> = {
  star: { en: 'STARS', the: 'STJERNENE', many: 'STJERNER' },
  tri: { en: 'TRIANGLES', the: 'TREKANTENE', many: 'TREKANTER' },
  plus: { en: 'PLUS SIGNS', the: 'PLUSSTEGNENE', many: 'PLUSSTEGN' },
};

/** Seconds a crate takes from leaving the left hatch to vanishing into the right one, at `speed` units a second. */
export const crossS = (speed: number) => (W - 2 * HATCH_W + CW) / speed;

/** The crates in sight `runT` seconds into a lot: across (units, their left side) and their mark's number (−1 plain). */
export function inSight(stock: Pick<Stock, 'crates' | 'enter'>, runT: number, speed: number): [x: number, mark: number][] {
  const out: [number, number][] = [];
  stock.crates.forEach((mark, i) => {
    const since = runT - stock.enter[i]!;
    if (since < 0) return;
    const x = HATCH_W - CW + since * speed;
    if (x > W - HATCH_W) return;
    out.push([q(x, 1), mark ? MARKS.indexOf(mark) : -1]);
  });
  return out;
}

/** Everything the store shows. */
export interface CountView {
  /** The game clock, ms. */
  t: number;
  /** The mark to count. */
  m: TargetMark;
  /** The crates in sight: across (units, their left side) and their mark's number in `MARKS` (−1 plain). */
  c: readonly (readonly [x: number, mark: number])[];
  /** How far the belt has run this round, units: its ridges and rollers move by it. */
  b: number;
  /** 0 rolling, 1 asking how many, 2 a wrong answer, 3 right. */
  p: 0 | 1 | 2 | 3;
  /** The numbers to tap, lowest and highest. */
  a: readonly [lo: number, hi: number];
  /** The number tapped, or null. */
  k: number | null;
  /** The right count, once it is answered (null until then). */
  n: number | null;
  fx?: Fx | undefined;
}

/** A star with `points` points, outer radius `r`, inner `ri`, centred on 0,0. */
function starPath(points: number, r: number, ri: number): string {
  const d: string[] = [];
  for (let k = 0; k < points * 2; k++) {
    const a = -Math.PI / 2 + (k * Math.PI) / points;
    const rr = k % 2 === 0 ? r : ri;
    d.push(`${k === 0 ? 'M' : 'L'}${(Math.cos(a) * rr).toFixed(2)} ${(Math.sin(a) * rr).toFixed(2)}`);
  }
  return `${d.join(' ')} Z`;
}

/** A plus of arm length `r` and thickness `t`, centred on 0,0. */
const plusPath = (r: number, t: number) => `M${-t} ${-r} H${t} V${-t} H${r} V${t} H${t} V${r} H${-t} V${t} H${-r} V${-t} H${-t} Z`;

/** One stencilled mark, centred on 0,0, about `r` from the centre to its tips. */
export function MarkShape({ mark, r, color }: { mark: Mark; r: number; color: string }) {
  switch (mark) {
    case 'star':
      return <path d={starPath(5, r, r * 0.46)} fill={color} />;
    case 'spark':
      return <path d={starPath(4, r, r * 0.4)} fill={color} />;
    case 'tri':
      return <path d={`M0 ${-r} L${r * 0.98} ${r * 0.72} L${-r * 0.98} ${r * 0.72} Z`} fill={color} />;
    case 'vee':
      return <path d={`M0 ${r} L${r * 0.98} ${-r * 0.72} L${-r * 0.98} ${-r * 0.72} Z`} fill={color} />;
    case 'plus':
      return <path d={plusPath(r * 0.92, r * 0.26)} fill={color} />;
    case 'cross':
      return <path d={plusPath(r * 0.98, r * 0.26)} fill={color} transform="rotate(45)" />;
    case 'dot':
      return <circle r={r * 0.72} fill={color} />;
    case 'ring':
      return <circle r={r * 0.64} fill="none" stroke={color} strokeWidth={r * 0.28} />;
    case 'square':
      return <rect x={-r * 0.66} y={-r * 0.66} width={r * 1.32} height={r * 1.32} rx={r * 0.12} fill={color} />;
  }
}

/** One crate on the belt, its top left at 0,0. */
function Crate({ era, mark, id }: { era: Era; mark: Mark | null; id: string }) {
  const pal = CRATE_PAL[era];
  const w = CW;
  const h = CH;
  const lid = h * 0.17;
  const plate = h * 0.33;
  const bracket = Math.min(8, w * 0.14);
  return (
    <g>
      <ellipse cx={w / 2} cy={h + 1.5} rx={w * 0.54} ry={3.2} fill="#000" opacity=".5" />
      <rect x="0" y="0" width={w} height={h} rx="3" fill={`url(#${id}crate)`} stroke={pal.edge} strokeWidth="1.2" />
      {/* the lid's rim and the battens down each side */}
      <rect x="0.6" y="0.6" width={w - 1.2} height={lid} rx="2.5" fill={pal.top} />
      <path d={`M${w * 0.09} ${lid + 1} V${h - 3} M${w * 0.91} ${lid + 1} V${h - 3}`} stroke={pal.edge} strokeWidth="2.2" opacity=".55" />
      {era === 'neon' && <rect x="1.5" y="1.5" width={w - 3} height={h - 3} rx="2.5" fill="none" stroke={pal.edge} strokeWidth="1" opacity=".55" />}
      {era === 'stars' && <rect x={w / 2 - 5} y="0.6" width="10" height={lid + 5} fill={shade(pal.top, 0.25)} opacity=".8" />}
      {era === 'debris' && <path d={`M3 ${h - 7} l5 -5 h4 l-5 5 z M12 ${h - 7} l5 -5 h4 l-5 5 z`} fill="#e3c14a" opacity=".7" />}
      {era === 'dust' && <path d={`M0 ${h * 0.62} Q${w / 2} ${h * 0.68} ${w} ${h * 0.62}`} stroke="#e8cf9a" strokeWidth="1.6" fill="none" opacity=".55" />}
      {era === 'canopy' && (
        <g opacity=".95">
          <ellipse cx={w * 0.3} cy="-2" rx="6" ry="3" fill="#7fc86a" transform={`rotate(-24 ${w * 0.3} -2)`} />
          <ellipse cx={w * 0.52} cy="-3.5" rx="6.5" ry="3" fill="#9ad874" transform={`rotate(12 ${w * 0.52} -3.5)`} />
          <ellipse cx={w * 0.72} cy="-1.5" rx="5" ry="2.6" fill="#6db35a" transform={`rotate(30 ${w * 0.72} -1.5)`} />
        </g>
      )}
      {/* corner brackets */}
      {[
        [0, 0, 1, 1],
        [w, 0, -1, 1],
        [0, h, 1, -1],
        [w, h, -1, -1],
      ].map(([x, y, sx, sy], i) => (
        <path
          key={i}
          d={`M${x! + sx! * 1.5} ${y! + sy! * bracket} V${y! + sy! * 1.5} H${x! + sx! * bracket}`}
          stroke={pal.metal}
          strokeWidth="2.2"
          fill="none"
          strokeLinecap="round"
          opacity=".85"
        />
      ))}
      {/* the soft gloss where the lamp catches it */}
      <rect x="3" y={lid + 2} width={w * 0.4} height="3" rx="1.5" fill="#fff" opacity=".1" />
      {mark && (
        <g transform={`translate(${w / 2} ${lid + (h - lid) / 2 + 1})`}>
          <circle r={plate} fill="#000" opacity=".26" />
          <MarkShape mark={mark} r={plate * 0.86} color={pal.mark} />
        </g>
      )}
    </g>
  );
}

/** The status line for each moment of a lot. */
function statusOf(view: CountView, lang: Lang) {
  const name = NAMES[view.m];
  const phase = Math.round(view.p);
  if (phase === 3) return { text: say(lang, `✓ ${view.n} — RIGHT`, `✓ ${view.n} – RIKTIG`), tone: 'ok' as const };
  if (phase === 2) return { text: say(lang, `IT WAS ${view.n} — AGAIN`, `DET VAR ${view.n} – PÅ NYTT`), tone: 'bad' as const };
  // No hints: the mark's name is the whole message, and a long one needs the line's width.
  if (phase === 1) return { text: say(lang, `HOW MANY ${name.en}?`, `HVOR MANGE ${name.many}?`) };
  return { text: say(lang, `COUNT THE ${name.en}`, `TELL ${name.the}`) };
}

/** The store, from its view state alone: the belt, the crates on it and the numbers below. */
function View({ view, era, accent, lang, input }: ViewProps<CountView>) {
  const id = useSvgId();
  const skin = ERA_SKIN[era];
  const pal = CRATE_PAL[era];
  const phase = Math.round(view.p);
  const shift = view.b;
  const [lo, hi] = view.a;
  const answers = Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
  const bw = (W - 2 * BTN_SIDE - BTN_GAP * (answers.length - 1)) / answers.length;
  const press = input?.press;

  return (
    <BoardFrame era={era} accent={accent} status={statusOf(view, lang)} fx={view.fx} input={input}>
      <defs>
        <linearGradient id={`${id}crate`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={shade(pal.body, 0.18)} />
          <stop offset="1" stopColor={shade(pal.body, -0.3)} />
        </linearGradient>
        <linearGradient id={`${id}frame`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#56626e" />
          <stop offset=".45" stopColor="#2c353f" />
          <stop offset="1" stopColor="#151b22" />
        </linearGradient>
        <linearGradient id={`${id}rubber`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3a434d" />
          <stop offset="1" stopColor="#161b21" />
        </linearGradient>
        <linearGradient id={`${id}hatch`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#020304" />
          <stop offset="1" stopColor="#0b0f14" />
        </linearGradient>
        <radialGradient id={`${id}lamp`} cx=".5" cy="0" r="1">
          <stop offset="0" stopColor={skin.haze} stopOpacity=".2" />
          <stop offset="1" stopColor={skin.haze} stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* the store behind: shelves of stock, faint */}
      <g opacity=".16" stroke={skin.haze} strokeWidth="1" fill="none">
        <path d={`M${HATCH_W + 8} 74 H${W - HATCH_W - 8} M${HATCH_W + 8} 112 H${W - HATCH_W - 8}`} />
        {[0.14, 0.24, 0.62, 0.7, 0.8].map((f, i) => (
          <rect key={f} x={f * W} y={i % 2 ? 56 : 58} width={i % 2 ? 22 : 16} height={i % 2 ? 18 : 16} rx="1.5" />
        ))}
        {[0.2, 0.33, 0.56, 0.74].map((f, i) => (
          <rect key={f} x={f * W} y={i % 2 ? 96 : 94} width={i % 2 ? 14 : 20} height={i % 2 ? 16 : 18} rx="1.5" />
        ))}
      </g>
      {/* the lamp over the belt */}
      <ellipse cx={W / 2} cy="0" rx={W * 0.55} ry={BELT_Y + 10} fill={`url(#${id}lamp)`} />

      {/* the sign: which mark to count */}
      <g transform={`translate(${W / 2} 0)`}>
        <path d="M-40 0 V10 M40 0 V10" stroke="#8795a2" strokeWidth="1.2" />
        <rect x="-64" y="10" width="128" height="38" rx="6" fill="#0a0e13" stroke={accent} strokeOpacity=".7" strokeWidth="1.3" />
        <text x="-8" y="34.5" textAnchor="end" fontSize={TYPE.label} fontWeight="700" letterSpacing="1.4" fontFamily={mono} fill={INK.text}>
          {say(lang, 'COUNT', 'TELL')}
        </text>
        <g transform="translate(24 29)">
          <MarkShape mark={view.m} r={13} color={accent} />
        </g>
      </g>

      {/* the crates on the belt */}
      {view.c.map(([x, m], i) => {
        // A slight bob as each crate rides over a roller.
        const bob = Math.sin((x / 22) * Math.PI) * 0.5;
        const mark = MARKS[Math.round(m)] ?? null;
        return (
          <g key={i} transform={`translate(${x.toFixed(1)} ${(BELT_Y - CH + bob).toFixed(2)})`}>
            <Crate era={era} mark={mark} id={id} />
          </g>
        );
      })}

      {/* the belt, its frame, rollers and legs */}
      <rect x="0" y={BELT_Y} width={W} height="9" fill={`url(#${id}rubber)`} />
      <g stroke="#4a5561" strokeWidth="2">
        {Array.from({ length: Math.ceil(W / RIDGE) + 2 }, (_, k) => {
          const x = k * RIDGE + (shift % RIDGE) - RIDGE;
          return <line key={k} x1={x} y1={BELT_Y + 1.5} x2={x} y2={BELT_Y + 7.5} />;
        })}
      </g>
      <rect x="0" y={BELT_Y + 9} width={W} height="18" fill={`url(#${id}frame)`} />
      <line x1="0" y1={BELT_Y + 9.5} x2={W} y2={BELT_Y + 9.5} stroke="#8795a2" strokeOpacity=".55" />
      {Array.from({ length: Math.floor((W - 2 * HATCH_W) / 42) + 1 }, (_, k) => {
        const cx = HATCH_W + 16 + k * 42;
        const cy = BELT_Y + 18;
        const deg = (((shift / ROLLER_R) * 180) / Math.PI) % 180;
        return (
          <g key={k} transform={`rotate(${deg.toFixed(1)} ${cx} ${cy})`}>
            <circle cx={cx} cy={cy} r={ROLLER_R} fill="#1b2229" stroke="#5f6c78" strokeWidth="1.2" />
            <line x1={cx} y1={cy - ROLLER_R + 1.2} x2={cx} y2={cy + ROLLER_R - 1.2} stroke="#8795a2" strokeWidth="1.2" />
            <circle cx={cx} cy={cy} r="1.6" fill="#8795a2" />
          </g>
        );
      })}
      {[0.2, 0.5, 0.8].map((f) => (
        <rect key={f} x={f * W - 4} y={BELT_Y + 27} width="8" height={SCENE_H - BELT_Y - 31} fill="#1e252d" stroke="#3a4450" />
      ))}
      <rect x="0" y={SCENE_H - 5} width={W} height="5" fill="#000" opacity=".35" />

      {/* the hatches the crates come out of and go into, with strip curtains */}
      {[0, W - HATCH_W].map((x, side) => {
        const top = BELT_Y - CH - 20;
        const edge = side ? x : x + HATCH_W;
        return (
          <g key={side}>
            <rect x={x} y={top} width={HATCH_W} height={BELT_Y + 9 - top} fill={`url(#${id}hatch)`} />
            {[0.14, 0.38, 0.62, 0.86].map((f) => (
              <rect key={f} x={x + f * HATCH_W - 2.6} y={top + 3} width="5.2" height={BELT_Y - top - 3} rx="1.5" fill={skin.haze} opacity=".1" />
            ))}
            <rect x={edge - 1.5} y={top} width="3" height={BELT_Y + 9 - top} fill={skin.ink} opacity=".6" />
            <path
              d={side ? `M${x - 7} ${top} L${x} ${top - 9} H${W} V${top} Z` : `M0 ${top - 9} H${HATCH_W} L${HATCH_W + 7} ${top} H0 Z`}
              fill="#2a323b"
              stroke={skin.ink}
              strokeOpacity=".55"
              strokeWidth="1"
            />
          </g>
        );
      })}

      {/* the numbers */}
      {answers.map((n, i) => {
        const right = phase === 3 && view.k === n;
        const wrong = phase === 2 && view.k === n;
        const shown = phase === 2 && view.n === n;
        const tone = right ? 'primary' : wrong ? 'bad' : shown ? 'ok' : 'plain';
        const ink = right ? INK.onFill : wrong ? BAD : shown ? OK : INK.hi;
        return (
          <BoardButton
            key={n}
            x={BTN_SIDE + i * (bw + BTN_GAP)}
            y={BTN_Y}
            w={bw}
            h={BTN_H}
            label={String(n)}
            accent={accent}
            tone={tone}
            down={view.k === n}
            dim={phase !== 1 && tone === 'plain'}
            onPress={press ? () => press(n) : undefined}
          >
            <text y={TYPE.big * 0.36} textAnchor="middle" fontFamily={mono} fontSize={TYPE.big} fontWeight="700" fill={ink}>
              {n}
            </text>
          </BoardButton>
        );
      })}
    </BoardFrame>
  );
}

type Phase = 'run' | 'ask' | 'wrong' | 'done';
const PHASE_CODE: Record<Phase, CountView['p']> = { run: 0, ask: 1, wrong: 2, done: 3 };

function Board(props: RoundProps) {
  const { level, lang } = props;
  const belt = BELTS[level]!;
  /** The belt's speed, units a second. */
  const speed = belt.speed * W;
  const row = answerRow(level);
  const [rand] = useState(() => seeded(Date.now() + level * 7919));
  const [first] = useState(() => dealStock(level, rand));
  const stock = useRef<Stock>(first);
  const phase = useRef<Phase>('run');
  const picked = useRef<number | null>(null);
  /** Seconds the belt has run this lot, and how far it has moved this round (units). */
  const runT = useRef(0);
  const beltX = useRef(0);
  const againAt = useRef(0);

  const round = useRound(props, (dt, now, won) => {
    if (won) return;
    if (phase.current === 'run') {
      runT.current += dt;
      beltX.current += speed * dt;
      const s = stock.current;
      const cross = crossS(speed);
      const passed = s.enter.filter((e) => runT.current > e + cross).length;
      round.progress((0.85 * passed) / s.crates.length);
      if (runT.current > s.enter[s.enter.length - 1]! + cross + 0.35) phase.current = 'ask';
    } else if (phase.current === 'wrong' && now >= againAt.current) {
      // A fresh lot rolls past.
      runT.current = 0;
      stock.current = dealStock(level, rand);
      picked.current = null;
      phase.current = 'run';
      round.progress(0);
    }
  });

  const answer = (n: number) => {
    if (!round.live || phase.current !== 'ask') return;
    picked.current = n;
    if (n === stock.current.count) {
      phase.current = 'done';
      round.win();
    } else {
      phase.current = 'wrong';
      againAt.current = round.time() + AGAIN_MS;
      round.miss();
    }
  };

  const s = stock.current;
  const answered = phase.current === 'wrong' || phase.current === 'done';
  const view: CountView = {
    t: q(round.now),
    m: s.target,
    c: inSight(s, runT.current, speed),
    b: q(beltX.current, 1),
    p: PHASE_CODE[phase.current],
    a: [row[0]!, row[row.length - 1]!],
    k: picked.current,
    n: answered ? s.count : null,
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Count the marked crates, then tap how many', 'Tell kassene med merket, og trykk på hvor mange'), press: answer }}
    />
  );
}

/**
 * The how-to: four crates roll past, two with the star, on a belt a little quicker than round 1's
 * so the loop stays short; the belt stops, and the finger taps 2. Worked out with the board's own
 * `inSight` and keyed every tenth of a second.
 */
function demoBelt(): DemoScript<CountView> {
  const stock = { crates: ['star', null, 'star', null] as const, enter: [0.3, 0.95, 1.6, 2.25] };
  const speed = 140;
  const askAt = Math.ceil((stock.enter[stock.enter.length - 1]! + crossS(speed) + 0.2) * 10) * 100;
  const keys: DemoKey<CountView>[] = [];
  for (let t = 0; t <= askAt; t += 100) {
    keys.push({
      at: t,
      view: { t: 0, m: 'star', c: inSight(stock, t / 1000, speed), b: q((speed * t) / 1000, 1), p: t < askAt ? 0 : 1, a: [1, 6], k: null, n: null },
    });
  }
  const tapAt = askAt + 900;
  keys.push({ at: tapAt, view: { p: 3, k: 2, n: 2, fx: [1, 0] }, ease: 'step' }, { at: tapAt + 500, view: { fx: [0, 0] } });
  // The button with 2 on it: the second of six.
  const bw = (W - 2 * BTN_SIDE - BTN_GAP * 5) / 6;
  const two = { x: q(BTN_SIDE + bw + BTN_GAP + bw / 2), y: BTN_Y + BTN_H / 2 };
  return {
    ms: tapAt + 1_800,
    keys,
    finger: [
      { at: 0, x: 220, y: 304, hide: true },
      { at: askAt - 300, x: 220, y: 304, hide: false },
      { at: tapAt - 250, x: two.x, y: two.y },
      { at: tapAt, x: two.x, y: two.y, down: true },
      { at: tapAt + 160, x: two.x, y: two.y, down: false },
      { at: tapAt + 1_200, x: 220, y: 304, hide: true },
    ],
    steps: [
      { at: 0, en: 'COUNT CRATES WITH THE MARK', no: 'TELL KASSENE MED MERKET' },
      { at: askAt - 400, en: 'THEN TAP HOW MANY', no: 'TRYKK SÅ PÅ HVOR MANGE' },
    ],
  };
}

export const game = thirtyGame<CountView>({
  Board,
  View,
  demo: demoBelt(),
  words: {
    en: {
      instruction: 'Count the crates with the mark as they roll past. Then tap how many.',
      howMove: 'Watch the belt and count the marked crates. When it stops, tap the number.',
    },
    no: {
      instruction: 'Tell kassene med merket mens de ruller forbi. Trykk så på hvor mange.',
      howMove: 'Følg båndet og tell kassene med merket. Når det stopper, trykk på tallet.',
    },
  },
});
